/**
 * Manejo de errores del segundo factor (TOTP), sin logica de UI. Antes las dos pantallas descartaban el error y decian
 * siempre "Codigo incorrecto"; ahora se distingue POR QUE fallo, con el codigo que entrega el backend (mfa.ts):
 *  - session_expired: el token pendiente dura 5 min y vencio (401) -> hay que volver a iniciar sesion;
 *  - code_expired:    el codigo fue valido hace 1-5 min (tardo en escribirlo, o el reloj del telefono va atrasado);
 *  - malformed:       no son 6 digitos;
 *  - wrong:           cualquier otro codigo. No se distingue "reloj adelantado" a proposito (seria explotable);
 *  - not_started:     se confirmo sin haber pedido la clave;
 *  - locked:          demasiados intentos (429): el servidor bloquea 15 min por usuario, aunque el codigo sea el correcto;
 *  - already_enrolled: la cuenta ya tiene segundo factor y no se le puede generar otra clave;
 *  - network / other: no llego al servidor / error inesperado.
 */
import { ApiError } from './backend';
import { errorMessage } from './errors';

export const CODE_LENGTH = 6;

export type MfaFailureKind =
  | 'session_expired' | 'code_expired' | 'malformed' | 'wrong' | 'not_started' | 'locked' | 'already_enrolled' | 'network' | 'other';

export interface MfaFailure {
  kind: MfaFailureKind;
  message: string;
}

const KIND_BY_CODE: Record<string, MfaFailureKind> = {
  code_expired: 'code_expired',
  malformed_code: 'malformed',
  invalid_code: 'wrong',
  not_started: 'not_started',
  already_enrolled: 'already_enrolled',
};

/** `expired` viene de un 401: en las rutas /mfa/* solo puede significar token pendiente vencido o invalido. */
export function classifyMfaError(e: unknown): MfaFailure {
  if (e instanceof ApiError) {
    // El bloqueo va por el estado HTTP y con mensaje propio: 15 minutos no se pueden leer como "codigo incorrecto".
    if (e.status === 429) return { kind: 'locked', message: 'Demasiados intentos con el código. Por seguridad, espere unos 15 minutos antes de volver a intentarlo.' };
    if (e.status === 401) return { kind: 'session_expired', message: 'Su verificación expiró (dura 5 minutos). Vuelva a iniciar sesión.' };
    return { kind: KIND_BY_CODE[e.code] ?? 'other', message: e.message };
  }
  return { kind: e instanceof TypeError ? 'network' : 'other', message: errorMessage(e) };
}

/** Lo que se hace con el campo tras un fallo: en estos casos el codigo escrito ya no sirve y se vuelve a pedir. */
export const CLEARS_CODE: ReadonlySet<MfaFailureKind> = new Set(['code_expired', 'wrong']);

/**
 * Normaliza lo que la persona escribe o pega: deja solo digitos (admite "123 456" o "123-456", que es como algunas apps
 * muestran el codigo) y avisa cuando algo se descarto que NO era un separador, en vez de comerse las teclas en silencio.
 */
export function normalizeCodeInput(raw: string): { code: string; note: string | null } {
  const digits = raw.replace(/\D/g, '');
  const stray = /[^\d\s-]/.test(raw);
  if (stray) return { code: digits.slice(0, CODE_LENGTH), note: 'El código lleva solo números.' };
  if (digits.length > CODE_LENGTH) return { code: digits.slice(0, CODE_LENGTH), note: `El código tiene ${CODE_LENGTH} dígitos: se ignoraron los sobrantes.` };
  return { code: digits, note: null };
}
